import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Chip
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { toast } from 'react-toastify';
import customizationApi from '../../utils/customizationApi';

const COMMITMENT_CONFIRM_LABEL =
  'I accept that customization metadata will reside in the same PostgreSQL database as core ERP. The table prefix and database placement choice will lock for routine edits; moving databases requires using the migration actions below.';

const CustomizeApplicationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deployed, setDeployed] = useState(true);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [databaseMode, setDatabaseMode] = useState('same');
  const [dedicatedDbName, setDedicatedDbName] = useState('');
  const [tablePrefix, setTablePrefix] = useState('custom_');
  const [settingsLocked, setSettingsLocked] = useState(false);
  const [commitmentAcceptedAt, setCommitmentAcceptedAt] = useState(null);
  const [modules, setModules] = useState({});

  const [commitmentModalOpen, setCommitmentModalOpen] = useState(false);
  const [commitmentChecked, setCommitmentChecked] = useState(false);

  const [migrateToDedOpen, setMigrateToDedOpen] = useState(false);
  const [migrateToCoreOpen, setMigrateToCoreOpen] = useState(false);
  const [dedUrlToMigrate, setDedUrlToMigrate] = useState('');
  const [migratePhraseToDed, setMigratePhraseToDed] = useState('');
  const [migratePhraseToCore, setMigratePhraseToCore] = useState('');
  const [migrateUrlOverrideCore, setMigrateUrlOverrideCore] = useState('');

  const load = async () => {
    setLoading(true);
    setDeployed(true);
    try {
      const [gateRes, appRes, modRes] = await Promise.all([
        customizationApi.get('/v1/admin/ui-gate'),
        customizationApi.get('/v1/admin/application-settings'),
        customizationApi.get('/v1/admin/module-settings')
      ]);
      const app = appRes.data?.data || {};
      setGlobalEnabled(!!app.customization_enabled);
      setDatabaseMode(app.database_mode || 'same');
      setDedicatedDbName(app.dedicated_database_name || '');
      setTablePrefix(app.table_prefix || 'custom_');
      setSettingsLocked(!!app.settings_locked);
      setCommitmentAcceptedAt(app.commitment_accepted_at || null);
      const rows = modRes.data?.data?.rows || [];
      const m = {};
      rows.forEach((r) => {
        m[r.module_code] = r.customization_enabled;
      });
      setModules(m);
      if (gateRes.data?.data) setDeployed(!!gateRes.data.data.customizationApiAvailable);
    } catch (e) {
      console.error(e);
      setDeployed(false);
      toast.error(
        e.response?.status === 503 || e.code === 'ERR_NETWORK'
          ? 'Customization API unreachable. Enable customization in Helm (docker-desktop) and rebuild images.'
          : e.response?.data?.message || 'Failed to load customization settings'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const requiresCommitmentModal = !settingsLocked && globalEnabled && databaseMode === 'same';

  const saveAppDirect = async (acceptCommitment) => {
    setSaving(true);
    try {
      await customizationApi.patch('/v1/admin/application-settings', {
        customization_enabled: globalEnabled,
        database_mode: databaseMode,
        dedicated_database_name: dedicatedDbName || null,
        table_prefix: tablePrefix,
        ...(acceptCommitment ? { accept_same_database_commitment: true } : {})
      });
      toast.success(
        acceptCommitment ? 'Commitment saved — storage settings are now locked.' : 'Application customization settings saved'
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('erp4c-customization-gate-refresh'));
      }
      setCommitmentModalOpen(false);
      setCommitmentChecked(false);
      load();
    } catch (e) {
      const code = e.response?.data?.code;
      if (code === 'CUSTOMIZATION_COMMITMENT_REQUIRED') {
        setCommitmentModalOpen(true);
      }
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApplicationClick = () => {
    if (requiresCommitmentModal) {
      setCommitmentChecked(false);
      setCommitmentModalOpen(true);
      return;
    }
    saveAppDirect(false);
  };

  const handleCommitmentConfirm = () => {
    if (!commitmentChecked) {
      toast.warning('Check the box to confirm.');
      return;
    }
    saveAppDirect(true);
  };

  const runMigrateToDedicated = async () => {
    setSaving(true);
    try {
      await customizationApi.post('/v1/admin/migrate/shared-to-dedicated', {
        dedicated_connection_url: dedUrlToMigrate.trim() || undefined,
        confirmation_phrase: migratePhraseToDed.trim()
      });
      toast.success('Migration to dedicated database completed. Follow on-screen steps and restart pods.');
      setMigrateToDedOpen(false);
      setDedUrlToMigrate('');
      setMigratePhraseToDed('');
      window.dispatchEvent(new Event('erp4c-customization-gate-refresh'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Migration failed');
    } finally {
      setSaving(false);
    }
  };

  const runMigrateToCore = async () => {
    setSaving(true);
    try {
      await customizationApi.post('/v1/admin/migrate/dedicated-to-shared', {
        dedicated_connection_url: migrateUrlOverrideCore.trim() || undefined,
        confirmation_phrase: migratePhraseToCore.trim()
      });
      toast.success('Migration back to core ERP database completed. Remove CUSTOMIZATION_DATABASE_URL and restart.');
      setMigrateToCoreOpen(false);
      setMigratePhraseToCore('');
      setMigrateUrlOverrideCore('');
      window.dispatchEvent(new Event('erp4c-customization-gate-refresh'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Migration failed');
    } finally {
      setSaving(false);
    }
  };

  const saveModules = async () => {
    setSaving(true);
    try {
      await customizationApi.patch('/v1/admin/module-settings', { modules });
      toast.success('Module toggles saved');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('erp4c-customization-gate-refresh'));
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const labels = {
    contacts: 'Contacts',
    accounts: 'Accounts',
    job_requirements: 'Job requirements',
    documents: 'Documents',
    timesheets: 'Timesheets',
    pto_requests: 'PTO requests',
    tickets: 'Tickets (Help desk)'
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!deployed) {
    return (
      <Box p={2}>
        <Alert severity="warning">
          The customization service is not available. For local Kubernetes, set{' '}
          <code>customization.enabled: true</code> in Helm values, run{' '}
          <code>./build-images.sh all</code> and <code>./apply-images.sh</code>, then restart pods.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TuneIcon /> Customize application settings
        {settingsLocked ? (
          <Chip icon={<LockIcon />} label="Locked" color="error" variant="outlined" size="small" />
        ) : null}
      </Typography>

      {settingsLocked && databaseMode === 'same' ? (
        <Alert severity="error" icon={<WarningAmberIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Critical — commitment locked
          </Typography>
          Customization metadata is stored in the <strong>same PostgreSQL database as core ERP</strong>. The{' '}
          <strong>table prefix</strong> and <strong>database placement</strong> cannot be changed through this screen.
          Use <strong>Move to dedicated database</strong> below when your organization is ready to isolate customization.
          {commitmentAcceptedAt ? (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Commitment recorded: {new Date(commitmentAcceptedAt).toLocaleString()}
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {settingsLocked && databaseMode === 'dedicated' ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Storage is <strong>locked</strong>. Customization metadata is configured for a <strong>dedicated</strong>{' '}
          database connection. Use <strong>Move back to core ERP database</strong> below only after backup and change
          control.
        </Alert>
      ) : null}

      <Typography variant="body2" color="text.secondary" paragraph>
        Enable customization for the tenant, choose database placement, and set a prefix for generated artifacts.
        When customization is on and you use the <strong>same database as core ERP</strong>, you must confirm a binding
        commitment — prefix and placement lock except via guided migration.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Application
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={globalEnabled} onChange={(e) => setGlobalEnabled(e.target.checked)} color="primary" />
            }
            label="Customization allowed for this application"
          />
          <Stack spacing={2} sx={{ mt: 2, maxWidth: 520 }}>
            <TextField
              select
              label="Customization database"
              value={databaseMode}
              onChange={(e) => setDatabaseMode(e.target.value)}
              disabled={settingsLocked}
              helperText={
                settingsLocked
                  ? 'Locked — use migration actions below to change deployment.'
                  : databaseMode === 'same'
                    ? 'Same PostgreSQL as core ERP requires acceptance dialog before lock.'
                    : 'Dedicated DB: set CUSTOMIZATION_DATABASE_URL on the customization-api deployment.'
              }
            >
              <MenuItem value="same">Same database as core ERP</MenuItem>
              <MenuItem value="dedicated">Dedicated database (ops sets connection URL)</MenuItem>
            </TextField>
            {databaseMode === 'dedicated' && (
              <TextField
                label="Dedicated database name (informational)"
                value={dedicatedDbName}
                onChange={(e) => setDedicatedDbName(e.target.value)}
                placeholder="erp4c_customization"
                disabled={settingsLocked}
                helperText="Typical name: erp4c_customization — must match the database in CUSTOMIZATION_DATABASE_URL."
              />
            )}
            <TextField
              label="Table prefix"
              value={tablePrefix}
              onChange={(e) => setTablePrefix(e.target.value)}
              disabled={settingsLocked}
              helperText={
                settingsLocked
                  ? 'Locked after commitment.'
                  : 'Used for generated custom-field storage artifacts. [a-z] start; trailing _ added if missing.'
              }
            />
          </Stack>
          <Button
            variant="contained"
            color={requiresCommitmentModal ? 'error' : 'primary'}
            startIcon={<SaveIcon />}
            sx={{ mt: 2 }}
            onClick={handleSaveApplicationClick}
            disabled={saving}
          >
            {requiresCommitmentModal ? 'Save & confirm commitment…' : 'Save application settings'}
          </Button>
        </CardContent>
      </Card>

      {settingsLocked ? (
        <Card sx={{ mb: 2, borderLeft: 4, borderColor: 'error.main' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SyncAltIcon /> Database migration (both directions)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Copy metadata between core ERP and a dedicated database. Backup both databases first. After each migration,
              follow the toast instructions (restart pods, update Helm env).
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {databaseMode === 'same' ? (
                <Button variant="outlined" color="error" onClick={() => setMigrateToDedOpen(true)}>
                  Move to dedicated database
                </Button>
              ) : (
                <Button variant="outlined" color="warning" onClick={() => setMigrateToCoreOpen(true)}>
                  Move back to core ERP database
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Modules
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Only modules switched on here can use Add Fields and participate in module relationships.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            {Object.keys(labels).map((code) => (
              <FormControlLabel
                key={code}
                control={
                  <Switch
                    checked={!!modules[code]}
                    onChange={(e) => setModules((prev) => ({ ...prev, [code]: e.target.checked }))}
                    disabled={!globalEnabled}
                  />
                }
                label={labels[code]}
              />
            ))}
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            Enable the application toggle above before enabling individual modules.
          </Alert>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={saveModules} disabled={saving || !globalEnabled}>
            Save module toggles
          </Button>
        </CardContent>
      </Card>

      <Dialog open={commitmentModalOpen} onClose={() => !saving && setCommitmentModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
          Confirm core ERP database commitment
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Customization metadata will live in the <strong>same PostgreSQL instance/database</strong> as core ERP.
            After you accept, <strong>you cannot change the table prefix or database placement here</strong> — only
            through <strong>Move to dedicated database</strong> when you choose to isolate customization later.
          </Alert>
          <Typography variant="body2" paragraph>
            {COMMITMENT_CONFIRM_LABEL}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={commitmentChecked} onChange={(e) => setCommitmentChecked(e.target.checked)} />}
            label="I understand and accept this commitment."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommitmentModalOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleCommitmentConfirm} disabled={saving}>
            Accept & lock
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={migrateToDedOpen} onClose={() => !saving && setMigrateToDedOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Move customization to dedicated database</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="error">
            This copies metadata into the dedicated URL you provide, then <strong>truncates</strong> customization tables
            on the core ERP database. Backup both databases first. Apply schema migration <strong>069/070</strong> on the
            target if empty.
          </Alert>
          <TextField
            label="Dedicated PostgreSQL URL"
            value={dedUrlToMigrate}
            onChange={(e) => setDedUrlToMigrate(e.target.value)}
            placeholder="postgresql://user:pass@host:5432/erp4c_customization"
            fullWidth
            helperText="Optional if CUSTOMIZATION_DATABASE_URL is already set on the pod (same URL should be used)."
          />
          <TextField
            label='Confirmation — type exactly: MIGRATE TO DEDICATED'
            value={migratePhraseToDed}
            onChange={(e) => setMigratePhraseToDed(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMigrateToDedOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={runMigrateToDedicated} disabled={saving}>
            Run migration
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={migrateToCoreOpen} onClose={() => !saving && setMigrateToCoreOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Move customization back to core ERP database</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="warning">
            Copies metadata into core ERP (from dedicated), then truncates customization tables on the dedicated
            database. Backup both. Then remove <code>CUSTOMIZATION_DATABASE_URL</code> from Helm and restart pods.
          </Alert>
          <TextField
            label="Dedicated PostgreSQL URL (source)"
            value={migrateUrlOverrideCore}
            onChange={(e) => setMigrateUrlOverrideCore(e.target.value)}
            placeholder="Leave blank if CUSTOMIZATION_DATABASE_URL is set on this pod"
            fullWidth
          />
          <TextField
            label='Confirmation — type exactly: MIGRATE TO CORE ERP'
            value={migratePhraseToCore}
            onChange={(e) => setMigratePhraseToCore(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMigrateToCoreOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={runMigrateToCore} disabled={saving}>
            Run migration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomizeApplicationSettings;
